import sound from '../components/home/images/icr.mp3';
import taskNotificationSound from '../components/home/sounds/pickup-change-notification.mp3';

// A global audio element for notifications
let audioElement = null;
let taskNotificationAudioElement = null;

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

// Flag to track if we're in consult call mode (continuous ringing)
let isConsultCallMode = false;

// Separate audio element for light beep sound (incoming call during active call)
let lightBeepInterval = null;
let lightBeepAudioContext = null;
let lightBeepBuffer = null;

/**
 * Initialize the notification handler
 * This should be called once when the app starts
 */
export const initNotificationHandler = () => {
  // Only run in browser environment
  if (!isBrowser) return;

  // Create audio element if it doesn't exist
  if (!audioElement) {
    audioElement = document.createElement('audio');
    audioElement.id = 'globalNotificationSound';
    audioElement.preload = 'auto';
    audioElement.loop = false; // Will be set to true for consult calls
    audioElement.src = sound; // Preload the sound so it's ready for first call
    document.body.appendChild(audioElement);
  }

  // Set up service worker message listener
  if (isBrowser && navigator.serviceWorker) {
    navigator.serviceWorker.onmessage = ({ data }) => {
      if (data && data.type === 'notificationArrived' && data.playSound) {
        // For service worker notifications, we assume they are consult calls that should loop
        playNotificationSound(false, undefined, true);
      }
    };
  }
};

/**
 * Play the notification sound
 * @param {boolean} pause - Whether to mute the audio briefly
 * @param {string} outputDeviceId - The ID of the audio output device (e.g. 'default' or custom sinkId)
 * @param {boolean} isConsultCall - Whether this is a consult call that should loop continuously
 */
export const playNotificationSound = async (pause = false, outputDeviceId, isConsultCall = false) => {
  if (!isBrowser || !audioElement) {
    isBrowser && console.error('Audio element not initialized. Call initNotificationHandler first.');
    return;
  }

  // If you want to explicitly tell this to pause/stop the sound, do it immediately and exit.
  if (pause) {
    audioElement.pause();
    audioElement.currentTime = 0; // Reset for the next play
    isConsultCallMode = false;
    audioElement.loop = false;
    return;
  }

  // Set consult call mode
  isConsultCallMode = isConsultCall;
  audioElement.loop = isConsultCall;

  audioElement.src = sound;

  // Read fresh device ID from localStorage if not explicitly provided
  const deviceId = outputDeviceId !== undefined ? outputDeviceId : localStorage.getItem("selectedConsultRingingDevice");

  if (typeof audioElement.setSinkId === 'function' && deviceId) {
    try {
      await audioElement.setSinkId(deviceId);
    } catch (err) {
      console.error('Failed to set audio output device for notification:', err);
    }
  } else if (!deviceId) {
    // No device selected, use system default (no warning needed)
  } else {
    console.warn('setSinkId is not supported in this browser');
  }

  audioElement.muted = pause;

  audioElement.play().catch(error => {
    console.error('Error playing notification sound:', error);
  });

  if (pause) {
    setTimeout(() => {
      pause && audioElement.pause();
      audioElement.muted = false;
    }, 1000);
  }
};

/**
 * Stop the notification sound
 */
export const stopNotificationSound = () => {
  if (!isBrowser || !audioElement) {
    isBrowser && console.error('Audio element not initialized. Call initNotificationHandler first.');
    return;
  }

  audioElement.pause();
  audioElement.currentTime = 0;
  isConsultCallMode = false;
  audioElement.loop = false;
};

/**
 * Check if we're currently in consult call mode (continuous ringing)
 * @returns {boolean} True if in consult call mode
 */
export const isInConsultCallMode = () => {
  return isConsultCallMode;
};

// Minimal silent WAV (44-byte header + 1 silent sample) used only for autoplay policy probing
const SILENT_WAV_URI = 'data:audio/wav;base64,UklGRiUAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQEAAACA';

/**
 * Test whether the browser's autoplay policy will permit audio playback.
 * Plays a silent one-sample WAV in a throwaway element so the main audioElement
 * is not affected. Returns true if play succeeds, false if blocked by autoplay policy.
 */
export const testAudioPlayAllowed = async () => {
  if (!isBrowser) return true;
  const testEl = document.createElement('audio');
  testEl.src = SILENT_WAV_URI;
  try {
    await testEl.play();
    testEl.pause();
    testEl.src = '';
    testEl.load();
    return true;
  } catch (e) {
    console.error('Error testing audio play allowed:', e);
    testEl.src = '';
    testEl.load();
    return e.name !== 'NotAllowedError';
  }
};

/**
 * Generate a simple beep tone using Web Audio API
 * Creates a gentle, non-intrusive beep sound
 * @returns {AudioBuffer} The generated audio buffer
 */
const generateBeepTone = (audioContext, frequency = 800, duration = 0.2, volume = 0.15) => {
  const sampleRate = audioContext.sampleRate;
  const numSamples = Math.floor(sampleRate * duration);
  const buffer = audioContext.createBuffer(1, numSamples, sampleRate);
  const data = buffer.getChannelData(0);

  for (let i = 0; i < numSamples; i++) {
    // Create a smooth sine wave with fade in/out to avoid clicks
    const t = i / sampleRate;
    const fadeIn = Math.min(1, t / 0.05); // 50ms fade in
    const fadeOut = Math.min(1, (duration - t) / 0.05); // 50ms fade out
    const envelope = fadeIn * fadeOut;
    data[i] = Math.sin(2 * Math.PI * frequency * t) * volume * envelope;
  }

  return buffer;
};

/**
 * Play a light beep sound for incoming calls during active consultation
 * This is a subtle, non-intrusive beep that plays periodically
 * Uses Web Audio API to generate a gentle beep tone
 * @param {string} outputDeviceId - The ID of the audio output device (not used for Web Audio, kept for API consistency)
 */
export const playLightBeepSound = async (outputDeviceId) => {
  if (!isBrowser) return;

  // Use Web Audio API to generate a simple beep
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      console.warn('Web Audio API not supported, falling back to silent notification');
      return;
    }

    // Initialize audio context and buffer if not already created
    if (!lightBeepAudioContext || lightBeepAudioContext.state === 'closed') {
      lightBeepAudioContext = new AudioContextClass();
      // Generate beep tone (800Hz, 0.2s duration, 15% volume) - gentle, non-intrusive
      lightBeepBuffer = generateBeepTone(lightBeepAudioContext, 800, 0.2, 0.15);
    }
    
    // Play beep function
    const playBeep = () => {
      if (!lightBeepAudioContext || !lightBeepBuffer) {
        console.warn('Light beep audio not initialized');
        return;
      }

      // Recreate if closed
      if (lightBeepAudioContext.state === 'closed') {
        try {
          lightBeepAudioContext = new AudioContextClass();
          lightBeepBuffer = generateBeepTone(lightBeepAudioContext, 800, 0.2, 0.15);
        } catch (err) {
          console.error('Failed to recreate audio context:', err);
          return;
        }
      }

      // Resume audio context if suspended (browser autoplay policy)
      if (lightBeepAudioContext.state === 'suspended') {
        lightBeepAudioContext.resume().catch(err => {
          console.error('Failed to resume audio context:', err);
        });
      }

      try {
        const source = lightBeepAudioContext.createBufferSource();
        const gainNode = lightBeepAudioContext.createGain();
        
        source.buffer = lightBeepBuffer;
        gainNode.gain.value = 0.15; // 15% volume for subtle beep
        
        source.connect(gainNode);
        gainNode.connect(lightBeepAudioContext.destination);
        
        source.start(0);
        source.onended = () => {
          source.disconnect();
          gainNode.disconnect();
        };
      } catch (err) {
        console.error('Error playing beep:', err);
      }
    };

    // Clear any existing interval
    if (lightBeepInterval) {
      clearInterval(lightBeepInterval);
    }

    // Play immediately, then every 12 seconds
    playBeep();
    lightBeepInterval = setInterval(playBeep, 12000); // 12 seconds interval

  } catch (error) {
    console.error('Error setting up light beep sound:', error);
    // Fallback: if Web Audio API fails, do nothing (silent notification)
  }
};

/**
 * Stop the light beep sound
 */
export const stopLightBeepSound = () => {
  if (!isBrowser) return;

  if (lightBeepInterval) {
    clearInterval(lightBeepInterval);
    lightBeepInterval = null;
  }

  // Note: Web Audio API sources are one-shot, so we just clear the interval
  // The audio context and buffer remain for future use
};

/**
 * Play a short one-shot sound for task notifications.
 * This does not start any interval, so it stays separate from consult-call sounds.
 */
export const playTaskNotificationSound = async () => {
  if (!isBrowser) return;

  try {
    if (!taskNotificationAudioElement) {
      taskNotificationAudioElement = document.createElement('audio');
      taskNotificationAudioElement.id = 'taskNotificationSound';
      taskNotificationAudioElement.preload = 'auto';
      taskNotificationAudioElement.loop = false;
      taskNotificationAudioElement.src = taskNotificationSound;
      document.body.appendChild(taskNotificationAudioElement);
    }

    taskNotificationAudioElement.pause();
    taskNotificationAudioElement.currentTime = 0;
    taskNotificationAudioElement.loop = false;
    await taskNotificationAudioElement.play();
  } catch (error) {
    console.error('Error playing task notification sound:', error);
  }
};

export default {
  initNotificationHandler,
  playNotificationSound,
  stopNotificationSound,
  isInConsultCallMode,
  playLightBeepSound,
  stopLightBeepSound,
  playTaskNotificationSound
}; 



// WEBPACK FOOTER //
// ./src/config/notification-handler.js